import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import { useTimeline } from '../contexts/wedding/useTimeline';
import { useResponsibilityTags } from '../contexts/wedding/useResponsibilityTags';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Edit, Trash, Clock, Calendar, List, GridMinus, Download,
  Phone, Mail, ChatBubble, OpenNewWindow, NavArrowDown, NavArrowUp, Send,
  MapPin, User, Group, Settings,
} from 'iconoir-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TIMELINE_CATEGORIES, getCategoryColor, getCategoryLabel, getCategoryIcon,
} from '../constants/timelineCategories';
import { ResponsibilityTagsManager } from '../components/timeline/ResponsibilityTagsManager';
import { TimelineFilters } from '../components/timeline/TimelineFilters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default empty filter state — also used when clearing.
const EMPTY_FILTERS = {
  search: '',
  categories: [],
  owner: '',
  teams: [],
  quickChip: '',  // 'bride' | 'groom' | 'planner' | 'photo_video'
};

// Per-wedding filter persistence — keyed so swapping weddings doesn't
// leak the previous wedding's filter selection.
const FILTERS_STORAGE_KEY = (weddingId) => `shaadi_timeline_filters_${weddingId}`;

// Format time for display
const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
};

// Duration in minutes
const getDuration = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
};

// Apply the active filter set to a list of timeline items. Pure helper —
// runs client-side because we already have the full per-event data loaded.
function applyFilters(items, filters) {
  return items.filter((item) => {
    // Free-text search across title / description / location.
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const blob = `${item.title || ''} ${item.description || ''} ${item.location || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    if (filters.categories.length && !filters.categories.includes(item.category)) {
      return false;
    }
    if (filters.owner && item.primary_owner !== filters.owner) {
      return false;
    }
    if (filters.teams.length) {
      const teams = item.supporting_teams || [];
      const hasAny = filters.teams.some((t) => teams.includes(t));
      if (!hasAny) return false;
    }
    if (filters.quickChip) {
      const owner = item.primary_owner || '';
      const teams = item.supporting_teams || [];
      const matchesAny = (names) => names.includes(owner) || names.some((n) => teams.includes(n));
      if (filters.quickChip === 'bride' && !matchesAny(['Bride', "Bride's Family", 'Bridal Party'])) return false;
      if (filters.quickChip === 'groom' && !matchesAny(['Groom', "Groom's Family", 'Groomsmen'])) return false;
      if (filters.quickChip === 'planner' && !matchesAny(['Planner', 'Coordinator'])) return false;
      if (filters.quickChip === 'photo_video' && !matchesAny(['Photography', 'Videography', 'Content Creator'])) {
        // Also accept items in those categories.
        if (item.category !== 'photography_video') return false;
      }
    }
    return true;
  });
}

export default function TimelinePage() {
  const { currentWedding, events, fetchEvents, vendors, fetchVendors } = useWedding();
  const [selectedEventId, setSelectedEventId] = useState('');
  const {
    items,
    createItem: createTimelineItem,
    updateItem: updateTimelineItem,
    deleteItem: deleteTimelineItem,
  } = useTimeline(currentWedding?.id, selectedEventId);
  const tagsApi = useResponsibilityTags(currentWedding?.id);
  const [weekData, setWeekData] = useState([]);
  const [viewMode, setViewMode] = useState('event'); // 'event', 'week-list', 'week-grid'
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', start_time: '09:00', end_time: '10:00',
    category: 'ceremony', primary_owner: '', supporting_teams: [], location: '',
  });
  const [contactsOpen, setContactsOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [showTagsManager, setShowTagsManager] = useState(false);

  // Filter state — persisted per wedding so users don't lose their setup
  // when bouncing between Timeline and other pages.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  useEffect(() => {
    if (!currentWedding?.id) return;
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY(currentWedding.id));
      setFilters(raw ? { ...EMPTY_FILTERS, ...JSON.parse(raw) } : EMPTY_FILTERS);
    } catch {
      setFilters(EMPTY_FILTERS);
    }
  }, [currentWedding?.id]);
  useEffect(() => {
    if (!currentWedding?.id) return;
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY(currentWedding.id), JSON.stringify(filters));
    } catch { /* ignore quota errors */ }
  }, [filters, currentWedding?.id]);

  const filteredItems = useMemo(() => applyFilters(items, filters), [items, filters]);

  const handleEmailContactSheet = async () => {
    if (!currentWedding) return;
    setEmailSending(true);
    try {
      const { data } = await axios.post(
        `${API}/contact-sheet/${currentWedding.id}/email`, {}, { withCredentials: true },
      );
      toast.success(`Sent to ${data?.sent_to || 'your email'} — check your inbox.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the contact sheet');
    } finally {
      setEmailSending(false);
    }
  };

  useEffect(() => {
    if (currentWedding) {
      fetchEvents(currentWedding.id);
      fetchVendors(currentWedding.id);
    }
  }, [currentWedding, fetchEvents, fetchVendors]);

  useEffect(() => {
    if (events.length > 0 && !selectedEventId) setSelectedEventId(events[0].id);
  }, [events, selectedEventId]);

  // Per-event items are managed by useTimeline; only the multi-event week view
  // needs its own fetch (different endpoint, different shape).
  const fetchWeekView = useCallback(async () => {
    if (!currentWedding) return;
    try {
      const { data } = await axios.get(`${API}/timeline/${currentWedding.id}`, { withCredentials: true });
      setWeekData(data);
    } catch (err) { console.error(err); }
  }, [currentWedding]);

  useEffect(() => {
    if (viewMode !== 'event') fetchWeekView();
  }, [viewMode, fetchWeekView]);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title, description: item.description || '',
        start_time: item.start_time, end_time: item.end_time, category: item.category,
        primary_owner: item.primary_owner || '',
        supporting_teams: item.supporting_teams || [],
        location: item.location || '',
      });
    } else {
      setEditingItem(null);
      const lastEnd = items.length > 0 ? items[items.length - 1].end_time : '09:00';
      const [h, m] = lastEnd.split(':').map(Number);
      const nextEnd = `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      setFormData({
        title: '', description: '', start_time: lastEnd, end_time: nextEnd, category: 'ceremony',
        primary_owner: '', supporting_teams: [], location: '',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateTimelineItem(editingItem.id, formData);
      } else {
        await createTimelineItem({
          wedding_id: currentWedding.id, event_id: selectedEventId, ...formData,
        });
      }
      setShowModal(false);
    } catch (err) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this timeline item?')) {
      await deleteTimelineItem(id);
    }
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);

  if (!currentWedding) {
    return <Layout><div className="text-center py-12 text-muted">Please select or create a wedding first.</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="timeline-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">Timeline</h1>
            <p className="text-muted font-light mt-2">Build hour-by-hour schedules for each event</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Download Timeline PDF */}
            {currentWedding && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/timeline/export/${currentWedding.id}/pdf`}
                className="btn-secondary flex items-center gap-1.5 no-underline text-xs px-3 py-2"
                data-testid="download-timeline-pdf-btn"
              >
                <Download className="w-3.5 h-3.5" /> Timeline PDF
              </a>
            )}
            {/* Download Contact Sheet PDF */}
            {currentWedding && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/contact-sheet/${currentWedding.id}/pdf`}
                className="btn-secondary flex items-center gap-1.5 no-underline text-xs px-3 py-2"
                data-testid="download-contact-sheet-pdf-btn"
              >
                <Download className="w-3.5 h-3.5" /> Contacts PDF
              </a>
            )}
            {/* Email contact sheet to my inbox */}
            {currentWedding && (
              <button
                type="button"
                onClick={handleEmailContactSheet}
                disabled={emailSending}
                className="btn-secondary flex items-center gap-1.5 text-xs px-3 py-2 disabled:opacity-60"
                data-testid="email-contact-sheet-btn"
              >
                <Send className="w-3.5 h-3.5" />
                {emailSending ? 'Sending…' : 'Email to me'}
              </button>
            )}
            {/* View Toggle */}
            <div className="flex bg-page border border-border rounded-sm overflow-hidden">
              <button onClick={() => setViewMode('event')}
                className={`px-3 py-2 text-xs flex items-center gap-1.5 transition-colors ${viewMode === 'event' ? 'bg-white text-charcoal shadow-sm' : 'text-muted hover:text-charcoal'}`}
                data-testid="view-event-btn">
                <Clock className="w-3.5 h-3.5" /> Event
              </button>
              <button onClick={() => setViewMode('week-list')}
                className={`px-3 py-2 text-xs flex items-center gap-1.5 transition-colors ${viewMode === 'week-list' ? 'bg-white text-charcoal shadow-sm' : 'text-muted hover:text-charcoal'}`}
                data-testid="view-week-list-btn">
                <List className="w-3.5 h-3.5" /> Week
              </button>
              <button onClick={() => setViewMode('week-grid')}
                className={`px-3 py-2 text-xs flex items-center gap-1.5 transition-colors ${viewMode === 'week-grid' ? 'bg-white text-charcoal shadow-sm' : 'text-muted hover:text-charcoal'}`}
                data-testid="view-week-grid-btn">
                <GridMinus className="w-3.5 h-3.5" /> Grid
              </button>
            </div>
          </div>
        </div>

        {/* ═══ EVENT VIEW ═══ */}
        {viewMode === 'event' && (
          <>
            <div className="flex items-center gap-3">
              <Select value={selectedEventId} onValueChange={(v) => { setSelectedEventId(v); }}>
                <SelectTrigger className="w-48 h-10" data-testid="timeline-event-select">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedEvent?.event_date && (
                <span className="text-sm text-muted flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(selectedEvent.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              )}
              <Button onClick={() => handleOpenModal()} className="btn-primary flex items-center gap-2 ml-auto" data-testid="add-timeline-item-btn">
                <Plus className="w-4 h-4" /> Add Item
              </Button>
              <Button
                onClick={() => setShowTagsManager(true)}
                className="btn-secondary flex items-center gap-2"
                data-testid="manage-tags-btn"
              >
                <Settings className="w-4 h-4" /> Tags
              </Button>
            </div>

            {/* Filter bar — drives `filteredItems` below */}
            <TimelineFilters
              filters={filters}
              onChange={setFilters}
              allTags={tagsApi.allTags}
              totalCount={items.length}
              visibleCount={filteredItems.length}
            />

            {/* Day-of Contacts collapsible section */}
            {selectedEvent && (
              <EventContactsSection
                event={selectedEvent}
                vendors={vendors || []}
                open={contactsOpen}
                onToggle={() => setContactsOpen((v) => !v)}
              />
            )}

            {items.length === 0 ? (
              <Card className="card-surface p-12 text-center">
                <Clock className="w-12 h-12 text-muted mx-auto mb-4" />
                <p className="text-muted mb-4">No timeline items for this event yet</p>
                <Button onClick={() => handleOpenModal()} className="btn-primary">Add First Item</Button>
              </Card>
            ) : filteredItems.length === 0 ? (
              <Card className="card-surface p-12 text-center" data-testid="no-filtered-items">
                <p className="text-muted mb-2">No items match your filters.</p>
                <button
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  className="text-sm text-terracotta hover:underline"
                >
                  Clear all filters
                </button>
              </Card>
            ) : (
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-[39px] top-0 bottom-0 w-px bg-border" />

                <div className="space-y-1">
                  {filteredItems.map((item) => {
                    const duration = getDuration(item.start_time, item.end_time);
                    const CategoryIcon = getCategoryIcon(item.category);
                    const catColor = item.color || getCategoryColor(item.category);
                    return (
                      <div key={item.id} className="flex gap-4 group" data-testid={`timeline-item-${item.id}`}>
                        {/* Time column */}
                        <div className="w-[80px] flex-shrink-0 text-right pt-3">
                          <span className="font-mono text-xs text-charcoal">{formatTime(item.start_time)}</span>
                        </div>

                        {/* Dot */}
                        <div className="relative flex-shrink-0 pt-4">
                          <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: catColor }} />
                        </div>

                        {/* Content */}
                        <Card
                          className="card-surface flex-1 p-4 mb-1 border-l-4"
                          style={{ borderLeftColor: catColor }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="font-sans text-charcoal font-medium">{item.title}</h3>
                                <Badge
                                  className="text-xs border py-0 inline-flex items-center gap-1"
                                  style={{ backgroundColor: `${catColor}15`, color: catColor, borderColor: `${catColor}30` }}
                                >
                                  <CategoryIcon className="w-3 h-3" strokeWidth={1.5} />
                                  {getCategoryLabel(item.category)}
                                </Badge>
                                {item.primary_owner && (
                                  <Badge
                                    className="text-xs border py-0 bg-ochre/10 text-ochre border-ochre/30 inline-flex items-center gap-1"
                                    data-testid={`item-owner-${item.id}`}
                                  >
                                    <User className="w-3 h-3" strokeWidth={1.5} />
                                    {item.primary_owner}
                                  </Badge>
                                )}
                              </div>
                              {item.description && <p className="text-sm text-muted">{item.description}</p>}
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted mt-1">
                                <span className="font-mono">
                                  {formatTime(item.start_time)} — {formatTime(item.end_time)}
                                  {duration > 0 && (
                                    <span className="ml-2">
                                      ({duration >= 60 ? `${Math.floor(duration / 60)}h ${duration % 60 > 0 ? `${duration % 60}m` : ''}` : `${duration}m`})
                                    </span>
                                  )}
                                </span>
                                {item.location && (
                                  <span className="inline-flex items-center gap-1 text-charcoal/70" data-testid={`item-location-${item.id}`}>
                                    <MapPin className="w-3 h-3" strokeWidth={1.5} />
                                    {item.location}
                                  </span>
                                )}
                              </div>
                              {(item.supporting_teams || []).length > 0 && (
                                <div className="flex flex-wrap items-center gap-1 mt-2" data-testid={`item-teams-${item.id}`}>
                                  <Group className="w-3 h-3 text-muted" strokeWidth={1.5} />
                                  {item.supporting_teams.map((t) => (
                                    <span
                                      key={t}
                                      className="inline-block bg-moss/10 text-moss border border-moss/30 px-1.5 py-px rounded-full text-[10px]"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => handleOpenModal(item)} className="p-1.5 text-muted hover:text-charcoal"><Edit className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                              <button onClick={() => handleDelete(item.id)} className="p-1.5 text-muted hover:text-terracotta"><Trash className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                            </div>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                  {/* End time */}
                  {filteredItems.length > 0 && (
                    <div className="flex gap-4">
                      <div className="w-[80px] flex-shrink-0 text-right pt-1">
                        <span className="font-mono text-xs text-muted">{formatTime(filteredItems[filteredItems.length - 1].end_time)}</span>
                      </div>
                      <div className="relative flex-shrink-0 pt-2">
                        <div className="w-2 h-2 rounded-full bg-border" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══ WEEK LIST VIEW ═══ */}
        {viewMode === 'week-list' && (
          <div className="space-y-8">
            {weekData.length === 0 ? (
              <Card className="card-surface p-12 text-center">
                <p className="text-muted">No timeline items created yet. Switch to Event view to start building timelines.</p>
              </Card>
            ) : (
              weekData.map((eventDay) => (
                <div key={eventDay.id}>
                  <div className="flex items-center gap-3 mb-4">
                    <h2 className="font-serif text-2xl text-charcoal">{eventDay.name}</h2>
                    {eventDay.event_date && (
                      <Badge variant="outline" className="font-mono text-xs">
                        {new Date(eventDay.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </Badge>
                    )}
                  </div>
                  {eventDay.items.length === 0 ? (
                    <p className="text-sm text-muted ml-4">No timeline items</p>
                  ) : (
                    <div className="space-y-2 ml-4">
                      {eventDay.items.map((item) => (
                        <div key={item.id} className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color || getCategoryColor(item.category) }} />
                          <span className="font-mono text-xs text-muted w-28 flex-shrink-0">
                            {formatTime(item.start_time)} — {formatTime(item.end_time)}
                          </span>
                          <span className="text-sm text-charcoal">{item.title}</span>
                          <Badge className="text-xs border py-0 ml-auto" style={{ backgroundColor: `${item.color || getCategoryColor(item.category)}15`, color: item.color || getCategoryColor(item.category), borderColor: `${item.color || getCategoryColor(item.category)}30` }}>
                            {getCategoryLabel(item.category)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* ═══ WEEK GRID VIEW ═══ */}
        {viewMode === 'week-grid' && (
          <div className="overflow-x-auto">
            {weekData.length === 0 ? (
              <Card className="card-surface p-12 text-center">
                <p className="text-muted">No timeline items created yet.</p>
              </Card>
            ) : (
              <div className="min-w-[800px]">
                {/* Grid header */}
                <div className="grid gap-3" style={{ gridTemplateColumns: `80px repeat(${weekData.length}, 1fr)` }}>
                  <div className="text-xs text-muted uppercase tracking-wider pt-2">Time</div>
                  {weekData.map((eventDay) => (
                    <div key={eventDay.id} className="text-center pb-3 border-b border-border">
                      <h3 className="font-serif text-sm text-charcoal">{eventDay.name}</h3>
                      {eventDay.event_date && (
                        <p className="text-xs text-muted font-mono">
                          {new Date(eventDay.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Time rows */}
                {Array.from({ length: 18 }, (_, i) => i + 6).map((hour) => (
                  <div key={hour} className="grid gap-3 border-b border-border/50" style={{ gridTemplateColumns: `80px repeat(${weekData.length}, 1fr)` }}>
                    <div className="font-mono text-xs text-muted py-2 text-right pr-2">
                      {formatTime(`${String(hour).padStart(2, '0')}:00`)}
                    </div>
                    {weekData.map((eventDay) => {
                      const hourItems = eventDay.items.filter(item => {
                        const startH = parseInt(item.start_time.split(':')[0]);
                        return startH === hour;
                      });
                      return (
                        <div key={eventDay.id} className="py-1 min-h-[36px]">
                          {hourItems.map((item) => (
                            <div
                              key={item.id}
                              className="px-2 py-1 rounded-sm text-xs mb-0.5 truncate"
                              style={{
                                backgroundColor: `${item.color || getCategoryColor(item.category)}15`,
                                borderLeft: `3px solid ${item.color || getCategoryColor(item.category)}`,
                                color: '#4A3E40'
                              }}
                              title={`${item.title} (${formatTime(item.start_time)} - ${formatTime(item.end_time)})`}
                            >
                              <span className="font-mono text-[10px] text-muted">{formatTime(item.start_time)}</span> {item.title}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingItem ? 'Edit Timeline Item' : 'Add Timeline Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input-field h-12" placeholder="e.g., Baraat Procession" required data-testid="timeline-title-input" />
            </div>

            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Description / Notes</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field h-12" placeholder="Optional details" data-testid="timeline-description-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Start Time</Label>
                <Input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="input-field h-12 font-mono" required data-testid="timeline-start-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">End Time</Label>
                <Input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="input-field h-12 font-mono" required data-testid="timeline-end-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="input-field h-12" data-testid="timeline-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {TIMELINE_CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    return (
                      <SelectItem key={c.value} value={c.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: c.color }} />
                          <span>{c.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Location <span className="text-muted normal-case font-light">(optional)</span></Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="input-field h-12"
                placeholder="e.g., Main hall, Bridal suite, Garden"
                data-testid="timeline-location-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Primary Owner</Label>
              <select
                value={formData.primary_owner}
                onChange={(e) => setFormData({ ...formData, primary_owner: e.target.value })}
                className="input-field h-12 w-full px-3 rounded-sm border border-border bg-white text-sm text-charcoal"
                data-testid="timeline-primary-owner-select"
              >
                <option value="">— No primary owner —</option>
                {tagsApi.allTags.filter((t) => t.kind === 'role').length > 0 && (
                  <optgroup label="Wedding roles">
                    {tagsApi.allTags.filter((t) => t.kind === 'role').map((t) => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
                {tagsApi.allTags.filter((t) => t.kind === 'vendor').length > 0 && (
                  <optgroup label="Vendor teams">
                    {tagsApi.allTags.filter((t) => t.kind === 'vendor').map((t) => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
                {tagsApi.allTags.filter((t) => t.kind === 'custom').length > 0 && (
                  <optgroup label="Custom">
                    {tagsApi.allTags.filter((t) => t.kind === 'custom').map((t) => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="space-y-2" data-testid="timeline-supporting-teams-section">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Supporting Teams</Label>
              <p className="text-[11px] text-muted">
                Tap to toggle. Anyone needed to make this moment happen.
              </p>
              {tagsApi.allTags.length === 0 ? (
                <p className="text-xs text-muted italic">No tags loaded yet…</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
                  {tagsApi.allTags.map((t) => {
                    const checked = formData.supporting_teams.includes(t.name);
                    return (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            supporting_teams: checked
                              ? prev.supporting_teams.filter((n) => n !== t.name)
                              : [...prev.supporting_teams, t.name],
                          }));
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-light tracking-wide border transition-colors ${
                          checked
                            ? 'bg-moss text-white border-moss'
                            : 'bg-white text-charcoal border-stone hover:border-charcoal'
                        }`}
                        data-testid={`timeline-team-toggle-${t.id}`}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowTagsManager(true)}
                className="text-[11px] text-terracotta hover:underline mt-1"
              >
                + Manage tags
              </button>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-timeline-btn">
                {editingItem ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ResponsibilityTagsManager
        open={showTagsManager}
        onOpenChange={setShowTagsManager}
        defaults={tagsApi.defaults}
        custom={tagsApi.custom}
        onCreate={tagsApi.createTag}
        onUpdate={tagsApi.updateTag}
        onDelete={tagsApi.deleteTag}
      />
    </Layout>
  );
}


// ─────────────────────── Day-of contacts section ───────────────────────
// Renders a collapsible panel listing the vendors linked to the currently-
// selected event. Each row has one-tap icons for Call, WhatsApp, Email, and
// Website — the exact four the bride / MOB actually reach for on the day.
function EventContactsSection({ event, vendors, open, onToggle }) {
  const linked = useMemo(
    () => vendors.filter((v) => (v.event_ids || []).includes(event.id)),
    [vendors, event.id],
  );
  const hasLinked = linked.length > 0;

  return (
    <Card className="card-surface overflow-hidden" data-testid="event-contacts-section">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-page/50 transition-colors"
        data-testid="event-contacts-toggle"
      >
        <div className="flex items-center gap-3">
          <Phone className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
          <span className="font-serif text-base text-charcoal">
            Day-of contacts for {event.name}
          </span>
          <Badge className="bg-ochre/10 text-ochre border border-ochre/30 text-xs font-normal">
            {linked.length} vendor{linked.length === 1 ? '' : 's'}
          </Badge>
        </div>
        {open ? (
          <NavArrowUp className="w-4 h-4 text-muted" />
        ) : (
          <NavArrowDown className="w-4 h-4 text-muted" />
        )}
      </button>

      {open && (
        <div className="border-t border-border divide-y divide-border">
          {!hasLinked ? (
            <p className="px-4 py-6 text-sm text-muted italic">
              No vendors linked to this event yet — head to <span className="text-terracotta">Vendors</span> and check the event box on each vendor to build your day-of contact sheet.
            </p>
          ) : (
            linked.map((v) => <VendorContactRow key={v.id} vendor={v} />)
          )}
        </div>
      )}
    </Card>
  );
}

function VendorContactRow({ vendor }) {
  const whatsappHref = whatsappLink(vendor.whatsapp || vendor.phone);
  const websiteHref = normalizeUrl(vendor.website);
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3"
      data-testid={`event-contact-${vendor.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-sans text-charcoal font-medium truncate">{vendor.name}</p>
        <p className="text-xs text-muted">
          {vendor.category || '—'}
          {vendor.contact_name && <span> · {vendor.contact_name}</span>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {vendor.phone && (
          <a href={`tel:${vendor.phone}`}
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-border rounded-sm hover:bg-page/50 hover:border-charcoal text-charcoal transition-colors"
            data-testid={`contact-call-${vendor.id}`}>
            <Phone className="w-3.5 h-3.5" /> Call
          </a>
        )}
        {whatsappHref && (
          <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-border rounded-sm hover:bg-page/50 hover:border-charcoal text-charcoal transition-colors"
            data-testid={`contact-whatsapp-${vendor.id}`}>
            <ChatBubble className="w-3.5 h-3.5" /> WhatsApp
          </a>
        )}
        {vendor.email && (
          <a href={`mailto:${vendor.email}`}
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-border rounded-sm hover:bg-page/50 hover:border-charcoal text-charcoal transition-colors"
            data-testid={`contact-email-${vendor.id}`}>
            <Mail className="w-3.5 h-3.5" /> Email
          </a>
        )}
        {websiteHref && (
          <a href={websiteHref} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-border rounded-sm hover:bg-page/50 hover:border-charcoal text-charcoal transition-colors"
            data-testid={`contact-website-${vendor.id}`}>
            <OpenNewWindow className="w-3.5 h-3.5" /> Site
          </a>
        )}
      </div>
    </div>
  );
}

function normalizeUrl(u) {
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

function whatsappLink(value) {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '';
}
